import { CalendarClock } from "lucide-react";
import { EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { launchBadge, priorityBadge, milestoneState, STATE_META } from "@/data/launchOptions";

const monthKey = (d) => (d ? d.slice(0, 7) : "No date");
const monthLabel = (key) => {
  if (key === "No date") return "No due date";
  try { return new Date(`${key}-01`).toLocaleString(undefined, { month: "long", year: "numeric" }); }
  catch { return key; }
};
const today = () => new Date().toISOString().slice(0, 10);

export const MilestoneTimeline = ({ milestones, onOpen }) => {
  if (milestones.length === 0) {
    return <EmptyState icon={CalendarClock} title="The launch route is clear" description="Add launch milestones with due dates to chart the expedition journey." />;
  }
  const t = today();
  const groups = {};
  [...milestones]
    .sort((a, b) => (a.due_date || "9999").localeCompare(b.due_date || "9999"))
    .forEach((m) => { const k = monthKey(m.due_date); (groups[k] = groups[k] || []).push(m); });

  const keys = Object.keys(groups);

  return (
    <div className="relative" data-testid="milestone-timeline">
      {/* the journey path */}
      <div className="absolute bottom-2 left-[15px] top-2 w-0.5 bg-gradient-to-b from-[#E0A82E] via-[#06B6D4] to-[#E4E9F3]" />
      <div className="space-y-8">
        {keys.map((key) => {
          const items = groups[key];
          const done = items.filter((m) => m.status === "Complete").length;
          return (
            <div key={key} data-testid={`timeline-month-${key}`} className="relative pl-10">
              {/* phase node */}
              <span className="absolute left-0 top-1 flex h-8 w-8 items-center justify-center rounded-full bg-[#14245F] font-numeric text-[11px] font-bold text-white ring-4 ring-[#F4F6FB]">
                {done}/{items.length}
              </span>
              <div className="mb-3">
                <h3 className="font-heading text-lg font-semibold text-[#14245F]">{monthLabel(key)}</h3>
                <p className="text-xs font-medium text-[#5A6B8C]">{items.length} milestone{items.length === 1 ? "" : "s"} · {done} completed</p>
              </div>
              <div className="space-y-2">
                {items.map((m) => {
                  const c = STATE_META[milestoneState(m, t)].color;
                  return (
                    <div key={m.id} role="button" tabIndex={0} aria-label={`View milestone: ${m.title}`} data-testid={`timeline-item-${m.id}`} onClick={() => onOpen?.(m)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen?.(m); } }} className="relative flex cursor-pointer flex-wrap items-center justify-between gap-2 overflow-hidden rounded-2xl border border-[#E4E9F3] bg-white px-4 py-2.5 pl-5 transition-all hover:-translate-y-0.5 hover:shadow-[0_8px_20px_rgba(20,36,95,0.10)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E0A82E]/50">
                      <span className="absolute inset-y-0 left-0 w-1.5" style={{ backgroundColor: c }} />
                      <div className="flex min-w-0 items-center gap-2">
                        <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: c }} />
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-[#14245F]">{m.title}</p>
                          <p className="text-xs text-[#5A6B8C]">{m.workstream || m.category} · {m.owner}{m.due_date ? ` · ${m.due_date}` : ""}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <StatusBadge status={priorityBadge(m.priority)} label={m.priority} />
                        <StatusBadge status={launchBadge(m.status)} label={m.status} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
